import { Steps } from "antd";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import Input from "../../../components/input";
import PhoneNumberInput from "../../../components/PhoneNumberInput";
import Select from "../../../components/Select";
import FileUpload from "../../../components/FileUpload";
import { fuelStationCreate } from "../../../store/Slices/FuelStationSlice";
import { fetchFuelCompaniesDropdown } from "../../../store/Slices/dropdownSlice";
import GoogleMapInput from "../../../components/GoogleMapInput";
import ReactQuill from "react-quill-new";
import { formats, modules, fuelStationInitialValues } from "../../../utils/helper";
import { FuelStationSchema } from "../../../utils/Schema";
import { successNotify, errorNotify } from "../../../utils/toast";
import { FiArrowLeft } from "react-icons/fi";

const FuelStationCreate = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [currentStep, setCurrentStep] = useState(0);

    const { fuelCompaniesDropdownData, loading: dropdownLoading } = useSelector(state => state.dropdowns);
    const { fuelStationCreateLoading } = useSelector(state => state.fuelStation);

    const [stationImage, setStationImage] = useState([]);
    const [stationDocument, setStationDocument] = useState([]);

    useEffect(() => {
        dispatch(fetchFuelCompaniesDropdown());
    }, [dispatch]);

    const fuelCompanyOptions = fuelCompaniesDropdownData?.map(item => ({
        label: item.name,
        value: item.id
    })) || [];

    const fuelTypeOptions = [
        { label: 'Petrol', value: 'petrol' },
        { label: 'Diesel', value: 'diesel' },
        { label: 'High Octane', value: 'high_octane' }
    ];

    const requiredStar = <span className="required_star">*</span>;

    const handleFormSubmit = async (values) => {
        const payload = {
            ...values,
            location: values.location_info?.station_point,
            lat: values.location_info?.station_latitude,
            lng: values.location_info?.station_longitude,
            station_image: stationImage[0]?.fileName || "",
            station_document: stationDocument[0]?.fileName || "",
            fuel_rate_at: values.fuel_rate_at || "",
        };
        delete payload.location_info;

        try {
            await dispatch(fuelStationCreate(payload)).unwrap();
            successNotify("Fuel Station created successfully");
            navigate("/fuel-stations");
        } catch (error) {
            errorNotify(error || "Failed to create fuel station");
        }
    };

    const stepFields = [
        ["name", "contact", "email", "fuel_company_id", "location_info"],
        ["contract_start_at", "contract_end_at", "rates"]
    ];

    const nextStep = async (validateForm, setTouched) => {
        const errors = await validateForm();
        const currentFields = stepFields[currentStep];

        const hasErrorInCurrentStep = currentFields.some(field => {
            if (field === "location_info") {
                return errors.location_info && Object.keys(errors.location_info).length > 0;
            }
            return !!errors[field];
        });

        if (!hasErrorInCurrentStep) {
            setCurrentStep(prev => prev + 1);
        } else {
            const touchedObj = {};
            currentFields.forEach(field => {
                if (field === "location_info") {
                    touchedObj.location_info = {
                        station_point: true,
                        station_latitude: true,
                        station_longitude: true
                    };
                } else {
                    touchedObj[field] = true;
                }
            });
            setTouched(touchedObj);
        }
    };

    const prevStep = () => setCurrentStep(prev => prev - 1);

    return (
        <div className="fuel_station_create">
            <Formik
                initialValues={fuelStationInitialValues}
                validationSchema={FuelStationSchema}
                onSubmit={handleFormSubmit}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                    validateForm,
                    setFieldTouched,
                    setTouched,
                    isValid
                }) => {
                    return (
                        <div>
                            {/* Desktop Header */}
                            <div className="desktop_header_only">
                                <Breadcrumbs values={['Fuel Management', 'Fuel Stations', 'Create Fuel Station']} />
                                <div className="page_header access_view">
                                    <h2>Create Fuel Station</h2>
                                    <div className="d-flex align-items-center gap-2">
                                        <Button
                                            cancelBtnText="Cancel"
                                            cancelBtn={() => navigate(-1)}
                                            submitText="Save Station"
                                            submitBtn={handleSubmit}
                                            loading={fuelStationCreateLoading}
                                            disabled={!isValid || fuelStationCreateLoading}
                                            type="button"
                                            shouldRow={false}
                                            isShowCancel={true}
                                            isShowSubmitBtn={currentStep === 1}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Mobile Header */}
                            <div className="mobile_header_only mobile_dispatch_header">
                                <div className="mobile_header_left_controls">
                                    <button className="mobile_menu_icon_btn" onClick={() => navigate(-1)} type="button" title="Back">
                                        <FiArrowLeft style={{ fontSize: '20px' }} />
                                    </button>
                                    <h1 className="mobile_header_title">Create Fuel Station</h1>
                                </div>
                                <div className="mobile_header_right_controls">
                                    {currentStep === 1 && (
                                        <button className="btn_figma_primary" type="button" onClick={handleSubmit} disabled={!isValid || fuelStationCreateLoading}>
                                            {fuelStationCreateLoading ? "Saving..." : "Save Station"}
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="fuel_station_layout">
                                <div className="fuel_station_card info_card">
                                    <div className="card_header">Station Information</div>
                                    <div className="card_body">
                                        <div className="steps_container">
                                            <Steps
                                                className="form_steps_vertical"
                                                current={currentStep}
                                                direction="vertical"
                                                items={[
                                                    { title: "General Details" },
                                                    { title: "Rates & Contracts" }
                                                ]}
                                            />
                                        </div>

                                        <div className="step_content">
                                            {currentStep === 0 && (
                                                <div className="step_form">
                                                    <Row>
                                                        <Col md={6}>
                                                            <Input
                                                                label="Station Name"
                                                                name="name"
                                                                placeholder="Enter station name"
                                                                value={values.name}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                error={errors.name}
                                                                touched={touched.name}
                                                                required
                                                            />
                                                        </Col>
                                                        <Col md={6}>
                                                            <PhoneNumberInput
                                                                label="Contact"
                                                                name="contact"
                                                                placeholder="923XXXXXXXXX"
                                                                value={values.contact}
                                                                setFieldValue={setFieldValue}
                                                                onBlur={handleBlur}
                                                                error={errors.contact}
                                                                touched={touched.contact}
                                                                required
                                                            />
                                                        </Col>
                                                        <Col md={6}>
                                                            <Input
                                                                label="Email"
                                                                name="email"
                                                                placeholder="Enter email address"
                                                                value={values.email}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                error={errors.email}
                                                                touched={touched.email}
                                                                required
                                                            />
                                                        </Col>
                                                        <Col md={6}>
                                                            <Select
                                                                label="Fuel Company"
                                                                name="fuel_company_id"
                                                                placeholder="Select fuel company"
                                                                options={fuelCompanyOptions}
                                                                value={fuelCompanyOptions.find(opt => opt.value === values.fuel_company_id)}
                                                                onChange={(opt) => setFieldValue('fuel_company_id', opt.value)}
                                                                onBlur={() => setFieldTouched('fuel_company_id', true)}
                                                                error={errors.fuel_company_id}
                                                                touched={touched.fuel_company_id}
                                                                loading={dropdownLoading}
                                                                required
                                                            />
                                                        </Col>
                                                        <Col md={12}>
                                                            <GoogleMapInput
                                                                label={<>Station Location {requiredStar}</>}
                                                                name="location_info"
                                                                value={values.location_info}
                                                                setFieldValue={setFieldValue}
                                                                error={errors.location_info}
                                                                touched={touched.location_info}
                                                                fieldPrefix="station"
                                                                required={false}
                                                            />
                                                        </Col>
                                                    </Row>

                                                    <div className="upload_section">
                                                        <div className="upload_group">
                                                            <label>Station Image</label>
                                                            <FileUpload
                                                                files={stationImage}
                                                                setFiles={setStationImage}
                                                                destination="fuel-stations"
                                                                maxFiles={1}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="form_actions">
                                                        <Button
                                                            submitText="Next Step"
                                                            submitBtn={() => nextStep(validateForm, setTouched)}
                                                            shouldRow={false}
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {currentStep === 1 && (
                                                <div className="step_form">
                                                    <Row className="g-3">
                                                        <Col md={4}>
                                                            <Input
                                                                label="Contract Start Date"
                                                                type="date"
                                                                name="contract_start_at"
                                                                value={values.contract_start_at}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                error={errors.contract_start_at}
                                                                touched={touched.contract_start_at}
                                                                required
                                                            />
                                                        </Col>
                                                        <Col md={4}>
                                                            <Input
                                                                label="Contract End Date"
                                                                type="date"
                                                                name="contract_end_at"
                                                                value={values.contract_end_at}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                error={errors.contract_end_at}
                                                                touched={touched.contract_end_at}
                                                                required
                                                            />
                                                        </Col>
                                                        <Col md={4}>
                                                            <Input
                                                                label="Fuel Rate Date"
                                                                type="date"
                                                                name="fuel_rate_at"
                                                                value={values.fuel_rate_at || ''}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                error={errors.fuel_rate_at}
                                                                touched={touched.fuel_rate_at}
                                                            />
                                                        </Col>
                                                    </Row>

                                                    <div className="rates_section">
                                                        <Row className="g-3">
                                                            <Col md={4}>
                                                                <Input
                                                                    label="Petrol Rate"
                                                                    name="rates[0].price"
                                                                    placeholder="Enter price"
                                                                    value={values.rates[0]?.price}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.rates?.[0]?.price}
                                                                    touched={touched.rates?.[0]?.price}
                                                                    required
                                                                />
                                                            </Col>
                                                            <Col md={4}>
                                                                <Input
                                                                    label="Diesel Rate"
                                                                    name="rates[1].price"
                                                                    placeholder="Enter price"
                                                                    value={values.rates[1]?.price}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.rates?.[1]?.price}
                                                                    touched={touched.rates?.[1]?.price}
                                                                    required
                                                                />
                                                            </Col>
                                                            <Col md={4}>
                                                                <Input
                                                                    label="High Octane Rate"
                                                                    name="rates[2].price"
                                                                    placeholder="Enter price"
                                                                    value={values.rates[2]?.price}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={errors.rates?.[2]?.price}
                                                                    touched={touched.rates?.[2]?.price}
                                                                    required
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </div>

                                                    <div className="upload_section">
                                                        <div className="upload_group">
                                                            <label>Station Documents / Contract</label>
                                                            <FileUpload
                                                                files={stationDocument}
                                                                setFiles={setStationDocument}
                                                                destination="fuel-stations"
                                                                maxFiles={1}
                                                            />
                                                        </div>
                                                    </div>


                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className='trip_notes_main_div'>
                                    <h5>Notes</h5>
                                    <div style={{ padding: '0 16px 16px 16px' }}>
                                        <ReactQuill
                                            placeholder={'Write notes here...'}
                                            modules={modules}
                                            theme="snow"
                                            value={values.notes || ''}
                                            formats={formats}
                                            onChange={(content) => setFieldValue('notes', content)}
                                            className='react_quill_input notes_quill_input quill-small-font trip_quill_input'
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                }}
            </Formik>
        </div>
    );
};

export default FuelStationCreate;
